import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, CurrencyCircleDollar } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO } from "./common";

export default function Purchases({ file }) {
  const key = `/accounting/files/${file.id}/purchases`;
  const { data: purchases = [] } = useSWR(key, fetcher);
  const { data: accounts = [] } = useSWR(`/accounting/files/${file.id}/accounts`, fetcher);
  const chargeAccounts = accounts.filter((a) => a.code.startsWith("6"));
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ supplier: "", ref: "", date: todayISO(), charge_account: "", ht: "", tva_applicable: true });

  const tva = form.tva_applicable ? (parseFloat(form.ht) || 0) * file.tva_rate / 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${file.id}/purchases`, {
        ...form, ref: form.ref || null, ht: parseFloat(form.ht),
      });
      toast.success("Achat comptabilisé (journal AC)");
      setOpen(false);
      setForm({ supplier: "", ref: "", date: todayISO(), charge_account: "", ht: "", tva_applicable: true });
      mutate(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
  };

  const pay = async (id) => {
    try { await api.post(`/accounting/files/${file.id}/purchases/${id}/pay`); toast.success("Paiement comptabilisé en banque"); mutate(key); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-600 max-w-xl">L'enregistrement génère l'écriture d'achat (journal AC : charge + TVA déductible ↔ fournisseur 401). Le paiement génère l'écriture de banque.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="new-purchase-btn"><Plus size={16} className="mr-1" /> Nouvel achat</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Facture fournisseur</DialogTitle>
              <DialogDescription>Compte de charge classes 60-68 du plan {file.chart_label}.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fournisseur *</Label><Input required value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="purchase-supplier" /></div>
                <div><Label>Réf. facture</Label><Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="purchase-date" /></div>
                <div><Label>Montant HT *</Label><Input required type="number" min="0" step="0.01" value={form.ht} onChange={(e) => setForm({ ...form, ht: e.target.value })} data-testid="purchase-ht" /></div>
              </div>
              <div>
                <Label>Compte de charge *</Label>
                <Input required list="charge-accounts" value={form.charge_account} onChange={(e) => setForm({ ...form, charge_account: e.target.value })} data-testid="purchase-account" placeholder="6053" />
                <datalist id="charge-accounts">{chargeAccounts.map((a) => (<option key={a.code} value={a.code}>{a.name}</option>))}</datalist>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.tva_applicable} onCheckedChange={(v) => setForm({ ...form, tva_applicable: !!v })} data-testid="purchase-tva" />
                TVA déductible au taux du dossier ({file.tva_rate}%) — soit {fmtN(tva)} {file.currency}
              </label>
              <div className="flex justify-between items-center border-t border-zinc-200 pt-3 text-sm">
                <span className="text-zinc-600">TTC : <span className="font-medium text-zinc-900">{fmtN((parseFloat(form.ht) || 0) + tva)}</span> {file.currency}</span>
                <Button type="submit" disabled={!form.supplier || !form.charge_account || !form.ht} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="purchase-submit">Comptabiliser</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-2">Date</th><th className="px-4 py-2">Fournisseur</th><th className="px-4 py-2">Réf.</th><th className="px-4 py-2">Compte</th>
              <th className="px-4 py-2 text-right">HT</th><th className="px-4 py-2 text-right">TVA</th><th className="px-4 py-2 text-right">TTC</th>
              <th className="px-4 py-2">Statut</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {purchases.length === 0 && <tr><td colSpan={9} className="p-6 text-zinc-500">Aucun achat enregistré.</td></tr>}
            {purchases.map((p) => (
              <tr key={p.id} data-testid={`purchase-row-${p.id}`}>
                <td className="px-4 py-2">{p.date}</td>
                <td className="px-4 py-2 font-medium">{p.supplier}</td>
                <td className="px-4 py-2 text-zinc-500">{p.ref || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.charge_account}</td>
                <td className="px-4 py-2 text-right">{fmtN(p.ht)}</td>
                <td className="px-4 py-2 text-right">{fmtN(p.tva)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtN(p.ttc)}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "payee" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                    {p.status === "payee" ? "Payée" : "À payer"}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  {p.status !== "payee" && <button onClick={() => pay(p.id)} className="text-emerald-700 hover:text-emerald-900" title="Marquer payée" data-testid={`purchase-pay-${p.id}`}><CurrencyCircleDollar size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
